/**
 * DefinitionValidator (AJIP-16.02.005 · WS2 · WS13)
 * ---------------------------------------------------------------------
 * Strict superset validator. Delegates the 9 engine-facing fields to
 * the existing validateJourneyDefinition (AJIP-16.02.003), then
 * validates the 25-field extended contract.
 */
import { validateJourneyDefinition } from '../core/JourneyConfig';
import {
  REQUIRED_FIELDS, MAX_DEFINITION_BYTES,
  REGISTRATION_STATUSES, DEPRECATION_STATUSES, FORBIDDEN_KEYS,
} from './JourneyDefinitionContract';
import {
  JOURNEY_TYPES, JOURNEY_CATEGORIES, JOURNEY_AUDIENCES,
} from './ClassificationModel';
import {
  InvalidJourneyDefinitionError,
  InvalidJourneyVersionError,
  InvalidClassificationError,
} from './RegistrationDiscoveryErrors';

const forbiddenLc = new Set(FORBIDDEN_KEYS.map((k) => k.toLowerCase()));
const isStringArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string');
const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;
const isIsoString = (v) => typeof v === 'string' && !Number.isNaN(Date.parse(v));

const walkForbidden = (value, path = 'definition') => {
  if (value === null || value === undefined || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((item, i) => walkForbidden(item, `${path}[${i}]`));
    return;
  }
  if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) {
    throw new InvalidJourneyDefinitionError(`${path} must be a plain object`);
  }
  for (const [k, v] of Object.entries(value)) {
    if (forbiddenLc.has(k.toLowerCase())) {
      throw new InvalidJourneyDefinitionError(`Forbidden key '${k}' at ${path}`);
    }
    if (typeof v === 'function') {
      throw new InvalidJourneyDefinitionError(`Executable code at ${path}.${k} is prohibited`);
    }
    walkForbidden(v, `${path}.${k}`);
  }
};

/**
 * Extract the 9 base fields from an extended definition so the existing
 * AJIP-16.02.003 validator can verify them. Preserves protection of the
 * Core Engine contract.
 */
const toBaseDefinition = (def) => ({
  identifier: def.journeyId,
  name: def.journeyName,
  description: def.journeyDescription,
  entryConditions: def.entryConditions,
  exitConditions: [],
  availableStates: def.supportedStates,
  transitions: def.transitionConfiguration,
  enabledFeatures: def.requiredFeatureFlags,
  requiredPermissions: def.permissionRequirements,
});

const CANONICAL_JOURNEY_FLAGS = new Set([
  'JOURNEYS_ENABLED', 'JOURNEYS_AI', 'JOURNEYS_MATCH',
  'JOURNEYS_PERSISTENCE', 'JOURNEYS_ANALYTICS', 'JOURNEYS_RECOMMENDATIONS',
  'JOURNEYS_FACTORY',
]);

const validateExtendedDefinition = (def) => {
  if (!def || typeof def !== 'object' || Array.isArray(def)) {
    throw new InvalidJourneyDefinitionError('Definition must be a plain object');
  }
  walkForbidden(def);

  // Payload cap (WS13).
  let serialised;
  try { serialised = JSON.stringify(def); } catch (_e) {
    throw new InvalidJourneyDefinitionError('Definition is not serialisable');
  }
  if (typeof serialised !== 'string') {
    throw new InvalidJourneyDefinitionError('Definition failed to serialise');
  }
  if (Buffer.byteLength(serialised, 'utf8') > MAX_DEFINITION_BYTES) {
    throw new InvalidJourneyDefinitionError(`Definition exceeds ${MAX_DEFINITION_BYTES} bytes`);
  }

  // Required fields.
  for (const f of REQUIRED_FIELDS) {
    if (!(f in def)) throw new InvalidJourneyDefinitionError(`Missing required field: ${f}`);
  }

  // Identity & version.
  if (!isNonEmptyString(def.journeyId)) {
    throw new InvalidJourneyDefinitionError('journeyId must be non-empty string');
  }
  if (!Number.isInteger(def.journeyVersion) || def.journeyVersion < 1) {
    throw new InvalidJourneyVersionError('journeyVersion must be integer >= 1');
  }
  if (!isNonEmptyString(def.journeyName)) {
    throw new InvalidJourneyDefinitionError('journeyName must be non-empty string');
  }
  if (typeof def.journeyDescription !== 'string') {
    throw new InvalidJourneyDefinitionError('journeyDescription must be string');
  }

  // Classification.
  if (!JOURNEY_TYPES.includes(def.journeyType)) {
    throw new InvalidClassificationError(`journeyType '${def.journeyType}' not recognised`);
  }
  if (!JOURNEY_CATEGORIES.includes(def.journeyCategory)) {
    throw new InvalidClassificationError(`journeyCategory '${def.journeyCategory}' not recognised`);
  }
  if (!isStringArray(def.journeyAudience) || def.journeyAudience.length === 0) {
    throw new InvalidClassificationError('journeyAudience must be non-empty string array');
  }
  for (const aud of def.journeyAudience) {
    if (!JOURNEY_AUDIENCES.includes(aud)) {
      throw new InvalidClassificationError(`journeyAudience '${aud}' not recognised`);
    }
  }

  if (!REGISTRATION_STATUSES.includes(def.registrationStatus)) {
    throw new InvalidJourneyDefinitionError(`registrationStatus '${def.registrationStatus}' invalid`);
  }

  // Feature flags — must be from the canonical set (WS2).
  if (!isStringArray(def.requiredFeatureFlags)) {
    throw new InvalidJourneyDefinitionError('requiredFeatureFlags must be string array');
  }
  for (const f of def.requiredFeatureFlags) {
    if (!CANONICAL_JOURNEY_FLAGS.has(f)) {
      throw new InvalidJourneyDefinitionError(`requiredFeatureFlags contains unknown flag '${f}'`);
    }
  }

  if (!isStringArray(def.entryConditions)) {
    throw new InvalidJourneyDefinitionError('entryConditions must be string array');
  }
  if (!isStringArray(def.eligibilityRules)) {
    throw new InvalidJourneyDefinitionError('eligibilityRules must be string array');
  }
  if (!isStringArray(def.permissionRequirements)) {
    throw new InvalidJourneyDefinitionError('permissionRequirements must be string array');
  }
  if (!isNonEmptyString(def.initialState)) {
    throw new InvalidJourneyDefinitionError('initialState must be non-empty string');
  }
  if (!isStringArray(def.supportedStates) || !def.supportedStates.includes(def.initialState)) {
    throw new InvalidJourneyDefinitionError('supportedStates must include initialState');
  }
  if (!Array.isArray(def.transitionConfiguration)) {
    throw new InvalidJourneyDefinitionError('transitionConfiguration must be array');
  }

  if (def.persistencePolicyRef !== null && typeof def.persistencePolicyRef !== 'string') {
    throw new InvalidJourneyDefinitionError('persistencePolicyRef must be string or null');
  }
  if (def.retentionPolicyRef !== null && typeof def.retentionPolicyRef !== 'string') {
    throw new InvalidJourneyDefinitionError('retentionPolicyRef must be string or null');
  }

  if (!Number.isInteger(def.priority)) {
    throw new InvalidJourneyDefinitionError('priority must be integer');
  }
  if (!isStringArray(def.tags)) {
    throw new InvalidJourneyDefinitionError('tags must be string array');
  }
  if (!isNonEmptyString(def.canonReference)) {
    throw new InvalidJourneyDefinitionError('canonReference must be non-empty string');
  }
  if (!isNonEmptyString(def.owner)) {
    throw new InvalidJourneyDefinitionError('owner must be non-empty string');
  }
  if (!isIsoString(def.createdAt)) {
    throw new InvalidJourneyDefinitionError('createdAt must be ISO string');
  }
  if (!isIsoString(def.updatedAt)) {
    throw new InvalidJourneyDefinitionError('updatedAt must be ISO string');
  }

  if (!DEPRECATION_STATUSES.includes(def.deprecationStatus)) {
    throw new InvalidJourneyDefinitionError(`deprecationStatus '${def.deprecationStatus}' invalid`);
  }
  if (def.deprecationStatus !== 'none') {
    if (def.replacementJourneyId !== null && !isNonEmptyString(def.replacementJourneyId)) {
      throw new InvalidJourneyDefinitionError('replacementJourneyId must be string or null');
    }
  } else if (def.replacementJourneyId !== null) {
    throw new InvalidJourneyDefinitionError('replacementJourneyId must be null when deprecationStatus is none');
  }

  // Now delegate the 9 engine-facing fields to the AJIP-16.02.003 validator.
  validateJourneyDefinition(toBaseDefinition(def));

  return true;
};

export { validateExtendedDefinition, toBaseDefinition, CANONICAL_JOURNEY_FLAGS };
